import {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useRef
} from 'react';
import type { ReactNode } from 'react';
import { useTimePunchClockApi } from '@src/api/useTimePunchClockApi';
import type {
    PunchErrorResponse,
    ErrorType,
    ClockUser,
    UserStatusResponse,
    PunchResponse,
    SuccessResponse
} from '@src/api/punchTypes';
import { punchFlowReducer } from '@src/components/PunchPad/punchFlowReducer';
import type {
    PunchFlowState,
    PunchFlowAction
} from '@src/components/PunchPad/punchFlowReducer';
import { hostBridge } from '@src/messaging/hostBridge';
import type {
    PunchErrorPayload,
    PunchSuccessPayload,
    EnrichedPunchSuccessPayload,
    ThinPunchSuccessPayload,
    OfflinePunchSuccessPayload
} from '@src/messaging/protocol';
import { useNetworkStatus } from '@src/context/NetworkStatusContext';
import { useOfflinePunch } from '@src/hooks/useOfflinePunch';
import type {
    OfflinePunchSelection,
    OfflinePunchResult
} from '@src/config/offlinePunchService';
import { useAuth } from '@src/auth/AuthContext';
import { useTerminalAccount } from '@src/context/AccountContext';
import type { TerminalAccount } from '@src/context/AccountContext';
import { fetchEnrichedPunch } from '@src/api/punchEnrichmentApi';
import type { EnrichedPunch } from '@src/api/punchEnrichmentApi';
import { resolveExternalRoleId } from '@src/config/roleMappingCache';

/** Punch types eligible for enrichment in this release (break punches stay thin — decision 2026-07-27). */
function isEnrichablePunchType(
    punchType: SuccessResponse['punchType']
): punchType is 'clock_in' | 'clock_out' {
    return punchType === 'clock_in' || punchType === 'clock_out';
}

function toThinPayload(
    response: SuccessResponse,
    userId: number
): ThinPunchSuccessPayload {
    return {
        punchType: response.punchType,
        punchId: response.timePunchId,
        punchedAt: response.punchedAt,
        userId
    };
}

function toEnrichedPayload(
    punchType: 'clock_in' | 'clock_out',
    punchId: number,
    userId: number,
    enriched: EnrichedPunch
): EnrichedPunchSuccessPayload {
    return {
        punchType,
        punchId,
        userId,
        externalUserId: enriched.externalUserId,
        clockedIn: enriched.clockedIn,
        clockedOut: enriched.clockedOut,
        roleId: enriched.roleId,
        externalRoleId: enriched.externalRoleId,
        shiftId: enriched.shiftId,
        departmentId: enriched.departmentId,
        locationId: enriched.locationId,
        tips: enriched.tips,
        breaks: enriched.breaks
    };
}

/**
 * Builds the offline `punch_success` payload (API-4753). Resolves
 * `externalRoleId` from the offline `role_mapping_cache` (API-4674) when a
 * role was selected; `resolveExternalRoleId` is fail-soft and never throws,
 * so a cache miss/error simply yields `null` rather than blocking the punch.
 */
async function buildOfflinePunchSuccessPayload(
    account: TerminalAccount,
    punchTime: Date,
    selection: OfflinePunchSelection
): Promise<OfflinePunchSuccessPayload> {
    const roleId = selection.roleId ?? null;
    const externalRoleId =
        roleId !== null
            ? await resolveExternalRoleId(
                  account.companyId,
                  account.locationId,
                  roleId
              )
            : null;

    return {
        offline: true,
        time: punchTime.toISOString(),
        companyId: account.companyId,
        locationHash: account.locationHash,
        roleId,
        externalRoleId,
        departmentId: selection.departmentId ?? null,
        tips: null
    };
}

function classifyThrownError(err: unknown): ErrorType {
    const msg = err instanceof Error ? err.message : String(err);
    if (
        err instanceof TypeError ||
        /failed to fetch|networkerror|network error|net::|eof|timeout/i.test(
            msg
        )
    ) {
        return 'network';
    }
    if (/\b5\d{2}\b/.test(msg) || /internal server error/i.test(msg)) {
        return 'server';
    }
    // Koin double-start errors are a distinct KMP-client failure mode
    // (rebuilding the client without a prior teardown, see API-4681) rather
    // than a generic app error — log distinctly to speed up diagnosis, even
    // though it still surfaces as 'unknown' to the user today.
    if (/koin|already started|alreadystarted/i.test(msg)) {
        console.error('[PunchFlowContext] Koin already-started error', err);
    }
    return 'unknown';
}

function mapErrorCategory(
    errorType: PunchErrorResponse['errorType']
): PunchErrorPayload['category'] {
    switch (errorType) {
        case 'invalid_punch_id':
        case 'break_too_short':
        case 'health_check_failed':
        case 'not_assigned_to_location':
        case 'clock_in_too_early':
        case 'shift_not_scheduled':
            return 'validation';
        case 'network':
            return 'network';
        case 'server':
            return 'server';
        default:
            return 'unknown';
    }
}

/**
 * Notifies the host once an offline dispatch attempt resolves (API-4753).
 * Mirrors the online path's success/error notification, but for the offline
 * queue: `'stored'` emits `punch_success` (not awaited, so it never blocks
 * dispatch/UI, same as the online success path); `'limitExceeded'`/`'error'`
 * emit `punch_error` for host parity. `'needsRoleSelection'` /
 * `'needsDepartmentSelection'` are intermediate states — nothing to notify.
 */
function emitOfflineResult(
    accountRef: { current: TerminalAccount | null },
    result: OfflinePunchResult,
    selection: OfflinePunchSelection
) {
    switch (result.type) {
        case 'stored': {
            const account = accountRef.current;
            if (!account) return;
            void buildOfflinePunchSuccessPayload(
                account,
                result.punchTime,
                selection
            ).then(payload => hostBridge.punchSuccess(payload));
            return;
        }
        case 'limitExceeded':
            hostBridge.punchError({
                category: 'validation',
                message: 'offline_limit_exceeded'
            });
            return;
        case 'error':
            hostBridge.punchError({
                category: 'unknown',
                message: result.reason
            });
            return;
        default:
            return;
    }
}


export interface ActionContext {
    user: ClockUser;
    actionLabel: string;
}

interface PunchFlowContextValue {
    state: PunchFlowState;
    dispatch: React.Dispatch<PunchFlowAction>;
    handlePin: (pin: string) => Promise<void>;
    actionContextRef: React.MutableRefObject<ActionContext | null>;
    lastStatusRef: React.MutableRefObject<UserStatusResponse | null>;
    handleIntermediateDismiss: () => void;
    handleOfflineSelection: (selection: OfflinePunchSelection) => Promise<void>;
    confirmOfflinePunch: () => Promise<void>;
}

const PunchFlowContext = createContext<PunchFlowContextValue | null>(null);

const IDLE: PunchFlowState = { phase: 'idle' };

export function PunchFlowProvider({ children }: { children: ReactNode }) {
    const { punch } = useTimePunchClockApi();
    const { isOnline } = useNetworkStatus();
    const offlinePunch = useOfflinePunch();
    const { accessToken } = useAuth();
    const { account } = useTerminalAccount();
    const [state, dispatch] = useReducer(punchFlowReducer, IDLE);

    const actionContextRef = useRef<ActionContext | null>(null);
    const lastExecuteRef = useRef<(() => Promise<PunchResponse>) | null>(null);
    const lastStatusRef = useRef<UserStatusResponse | null>(null);

    // Kept fresh on every render (mirrors useTimePunchClockApi's tokenRef
    // pattern) so the async enrichment call below always reads the current
    // session, even though it resolves well after this render.
    const accessTokenRef = useRef(accessToken);
    accessTokenRef.current = accessToken;
    const accountRef = useRef(account);
    accountRef.current = account;
    const isOnlineRef = useRef(isOnline);
    isOnlineRef.current = isOnline;

    // Track the last known user status so the page can show it as a backdrop
    // during showing_intermediate and executing_action phases.
    if (state.phase === 'showing_status') {
        lastStatusRef.current = state.response;
    }

    // Best-effort: resolve the enriched payload for clock_in/clock_out
    // (bounded by ENRICHMENT_TIMEOUT_MS inside fetchEnrichedPunch), falling
    // back to the thin payload on any failure, timeout, offline, or missing
    // scope (company/location/applicationName/token). Never throws — this is
    // fire-and-forget from the caller's perspective, deliberately not awaited
    // before EXECUTION_COMPLETE dispatches, so the employee's own success UI
    // (driven by KMP/reducer state) is never gated on it — only the host
    // event is briefly delayed (decision 2026-07-27).
    async function resolvePunchSuccessPayload(
        response: SuccessResponse,
        userId: number
    ): Promise<PunchSuccessPayload> {
        const thin = toThinPayload(response, userId);
        if (!isEnrichablePunchType(response.punchType)) return thin;
        // Already offline: skip the network round-trip entirely rather than
        // waiting out ENRICHMENT_TIMEOUT_MS on a call known to fail.
        if (!isOnlineRef.current) return thin;

        const token = accessTokenRef.current;
        const account = accountRef.current;
        if (!token || !account || !account.applicationName) return thin;

        try {
            const enriched = await fetchEnrichedPunch(
                account.companyId,
                account.locationId,
                account.applicationName,
                response.timePunchId,
                token
            );
            return toEnrichedPayload(
                response.punchType,
                response.timePunchId,
                userId,
                enriched
            );
        } catch (err) {
            console.error(
                '[PunchFlowContext] Punch enrichment failed; falling back to thin payload',
                err
            );
            return thin;
        }
    }

    useEffect(() => {
        // Reset the run-once guard whenever we leave executing_action so that
        // re-selecting an action reusing the same `execute` reference (e.g. the
        // shift's action.execute after dismissing a health-check/intermediate
        // modal, which restores the same UserStatusResponse) runs again rather
        // than being wrongly skipped, leaving the flow stuck loading (API-4795).
        if (state.phase !== 'executing_action') {
            lastExecuteRef.current = null;
            return;
        }
        if (lastExecuteRef.current === state.execute) return;
        lastExecuteRef.current = state.execute;

        const { execute } = state;
        const userId = actionContextRef.current?.user.id ?? 0;

        execute()
            .then(response => {
                if (response.type === 'success') {
                    // Not awaited: dispatch below (which drives the employee's
                    // own success UI) must not wait on the network round-trip.
                    void resolvePunchSuccessPayload(response, userId).then(
                        payload => hostBridge.punchSuccess(payload)
                    );
                } else if (response.type === 'error') {
                    hostBridge.punchError({
                        category: mapErrorCategory(response.errorType),
                        message: response.message ?? response.errorType
                    });
                }
                dispatch({ type: 'EXECUTION_COMPLETE', response });
            })
            .catch((err: unknown) => {
                const errorType = classifyThrownError(err);
                const result: PunchErrorResponse = {
                    type: 'error',
                    errorType,
                    message: null
                };
                hostBridge.punchError({
                    category: mapErrorCategory(errorType),
                    message: errorType
                });
                dispatch({ type: 'EXECUTION_COMPLETE', response: result });
            });
    }, [state]);

    async function handlePin(pin: string) {
        // Offline dispatch (API-4650): intercept before KMP is ever invoked.
        // PunchFlowService.start() must never run while offline — the entire
        // offline path stays within this React layer.
        if (!isOnline) {
            dispatch({ type: 'OFFLINE_PIN_ENTERED', pin });
            return;
        }

        if (!punch) return;
        dispatch({ type: 'SUBMIT_PIN' });
        try {
            const response = await punch(pin);
            if (response.type === 'user_status') {
                dispatch({ type: 'STATUS_RECEIVED', response });
            } else if (response.type === 'error') {
                dispatch({ type: 'STATUS_ERROR', result: response });
            } else {
                dispatch({ type: 'EXECUTION_COMPLETE', response });
            }
        } catch (err) {
            const result: PunchErrorResponse = {
                type: 'error',
                errorType: classifyThrownError(err),
                message: null
            };
            dispatch({ type: 'STATUS_ERROR', result });
        }
    }

    // Resumes an offline punch once a role or department has been selected.
    // API-4645 owns the selection modal itself; this just completes the
    // dispatch logic once a choice is made.
    async function handleOfflineSelection(selection: OfflinePunchSelection) {
        if (
            state.phase !== 'offline_role_selection' &&
            state.phase !== 'offline_department_selection'
        ) {
            return;
        }
        const { pin } = state;
        const result = await offlinePunch.completeOfflinePunch(pin, selection);
        emitOfflineResult(accountRef, result, selection);
        dispatch({ type: 'OFFLINE_RESULT', pin, result });
    }

    function handleIntermediateDismiss() {
        if (lastStatusRef.current) {
            dispatch({
                type: 'STATUS_RECEIVED',
                response: lastStatusRef.current
            });
        } else {
            dispatch({ type: 'DISMISS' });
        }
    }

    // Confirms the offline intermediate page's "Punch offline" tap, running
    // the same offline dispatch logic previously invoked immediately on PIN
    // entry (API-4650), now gated behind this explicit user confirmation.
    async function confirmOfflinePunch() {
        if (state.phase !== 'offline_intermediate') return;
        // API-4380: reconnecting from any offline state must clear the offline
        // UX and return to the normal online punch pad. If we're back online by
        // the time the user confirms, reset instead of running offline dispatch.
        if (isOnline) {
            dispatch({ type: 'DISMISS' });
            return;
        }
        const { pin } = state;
        const result = await offlinePunch.handleOfflinePunch(pin);
        emitOfflineResult(accountRef, result, {});
        dispatch({ type: 'OFFLINE_RESULT', pin, result });
    }

    return (
        <PunchFlowContext.Provider
            value={{
                state,
                dispatch,
                handlePin,
                actionContextRef,
                lastStatusRef,
                handleIntermediateDismiss,
                handleOfflineSelection,
                confirmOfflinePunch
            }}
        >
            {children}
        </PunchFlowContext.Provider>
    );
}

export function usePunchFlow(): PunchFlowContextValue {
    const ctx = useContext(PunchFlowContext);
    if (!ctx) {
        throw new Error('usePunchFlow must be used within a PunchFlowProvider');
    }
    return ctx;
}
